// src/features/media/components/VideoPreview.tsx

import { AspectRatio, Center, Stack, Text, Loader } from '@mantine/core';
import { IconAlertCircle, IconUpload } from '@tabler/icons-react';
import { MediaStatusType } from '@lib2/model/media/MediaTypes';
import { MediaPlayerLinker } from '@lib2/model/player/PlayerLinker';
import { Hostname } from '@lib2/model/common/ServiceHosts';
import { usePlayers } from '@/features/players/usePlayers';
import type { VideoViewData } from '@/lib/types/Media';
import type IMediaView from '@lib2/model/media/IMediaView';

interface VideoPreviewProps {
  video: VideoViewData;
}

export function VideoPreview({ video }: VideoPreviewProps) {
  const { defaultPlayerId } = usePlayers();
  const status = video.media.mediaStatusType;

  // Ready: show player iframe
  if (status === MediaStatusType.Ready && defaultPlayerId) {
    const mediaView = { data: video } as unknown as IMediaView;
    const isReseller = Hostname.isResellerHostname();
    const linker = new MediaPlayerLinker(defaultPlayerId, mediaView, isReseller);
    const previewUrl = linker.getPreviewUrl(false, { isPreview: true });

    return (
      <AspectRatio ratio={16 / 9}>
        <iframe
          src={previewUrl}
          style={{ border: 'none', width: '100%', height: '100%' }}
          allowFullScreen
          title={video.media.title}
        />
      </AspectRatio>
    );
  }

  // Not ready: show status placeholder
  return (
    <AspectRatio ratio={16 / 9}>
      <Center bg="gray.1" style={{ borderRadius: 8 }}>
        <Stack align="center" gap="sm">
          {status === MediaStatusType.Error ? (
            <>
              <IconAlertCircle size={48} color="var(--mantine-color-red-6)" />
              <Text c="red.6" fw={500}>Error processing video</Text>
            </>
          ) : status === MediaStatusType.AwaitingIngest ? (
            <>
              <IconUpload size={48} color="var(--mantine-color-gray-5)" />
              <Text c="dimmed">Awaiting upload</Text>
            </>
          ) : (
            <>
              <Loader size="lg" />
              <Text c="dimmed">
                {status === MediaStatusType.Processing ? 'Processing video...' : 'Ingesting video...'}
              </Text>
            </>
          )}
        </Stack>
      </Center>
    </AspectRatio>
  );
}
